import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import AgreementModal from '../agreements/AgreementModal';
import api from '../../services/api';
import { Box, CircularProgress } from '@mui/material';

interface RequireAgreementsProps {
  children: React.ReactNode;
}

const RequireAgreements: React.FC<RequireAgreementsProps> = ({ children }) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [checkComplete, setCheckComplete] = useState(false);
  const [needsAgreement, setNeedsAgreement] = useState(false);
  const [agreementStatus, setAgreementStatus] = useState<any>(null);

  useEffect(() => {
    // Skip agreement checks for service routes
    const currentPath = window.location.pathname;
    const isServiceRoute = ['/shuttle-booking', '/reminders/dashboard', '/marketplace', '/services'].some(
      path => currentPath.startsWith(path)
    );
    
    if (isServiceRoute) {
      setCheckComplete(true);
      return;
    }
    
    if (user?.id) {
      checkAgreementStatus();
    } else {
      setCheckComplete(true);
    }
  }, [user?.id]);

  const checkAgreementStatus = async () => {
    if (!user?.id) {
      setCheckComplete(true);
      return;
    }

    try {
      // Get both agreement status and dashboard type for accurate user role detection
      const [agreementResponse, dashboardResponse] = await Promise.all([
        api.get(`/users/${user.id}/agreement-status`),
        api.get('/me/dashboard-type').catch(() => null)
      ]);
      
      setAgreementStatus(agreementResponse.data);
      
      // Determine if user is a pilot using multiple sources
      // 1. Check user object properties
      // 2. Check dashboard type response
      // 3. Check if on pilot-dashboard route
      const isPilot = 
        user.is_pilot === true || 
        user.user_type === 'pilot' ||
        dashboardResponse?.data?.dashboard_type === 'pilot' ||
        window.location.pathname.includes('pilot-dashboard');
      
      
      // Pilots only need privacy policy, others need both agreements
      const allAccepted = isPilot 
        ? agreementResponse.data.privacy_policy?.accepted === true
        : agreementResponse.data.all_agreements_accepted === true;
        
      setNeedsAgreement(!allAccepted);
    } catch (error) {
      console.error('Failed to check agreement status:', error);
      // In case of error, allow access but log it
      setNeedsAgreement(false);
    } finally {
      setCheckComplete(true);
    }
  };

  const handleAgreementAccept = async () => {
    setNeedsAgreement(false);
    // Force a page refresh to update all components
    window.location.reload();
  };

  if (!checkComplete) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {children}
      {user && needsAgreement && checkComplete && (
        <AgreementModal
          open={true}
          userId={user.id}
          onAccept={handleAgreementAccept}
        />
      )}
    </>
  );
};

export default RequireAgreements;